import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useAuth';
import { fieldExecutiveMayOpen } from '@/routes/paths';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { ContentContainer } from './ContentContainer';
import { OverlayManager } from '@/shared/components/Modal/ModalManager';
import { usePresenceHeartbeat } from '@/shared/hooks/usePresenceHeartbeat';
import { StarterTour } from '@/shared/components/StarterTour';
import { RightCopilot } from '@/shared/components/RightCopilot';
import { isEmbeddedApp } from '@/shared/utils/embedded';

/**
 * Authenticated application frame. The blueprint shell remains the single
 * composition owner for the sidebar, top navigation, routed content, and
 * overlay planes.
 */
export function AppShell() {
  usePresenceHeartbeat();
  const user = useUser();
  const { pathname } = useLocation();
  // A Field Executive sees only their field screens (2026-10-07) — any other
  // address (typed, bookmarked, or from an old link) goes back to their
  // dashboard. The server enforces the same on every API.
  if (user?.role === 'field_executive' && !fieldExecutiveMayOpen(pathname)) return <Navigate to="/dashboard" replace />;
  // Inside the Android app the app's own header and menu take their place.
  const embedded = isEmbeddedApp();
  return (
    <>
      <div
        className="flex h-screen flex-col overflow-hidden bg-surface-container-low"
        data-shell="enterprise"
      >
        {!embedded && <Topbar />}
        <div className="flex min-w-0 flex-1 overflow-hidden">
          {!embedded && <Sidebar />}
          <main className="flex-1 overflow-y-auto overflow-x-auto min-w-0" data-shell-plane="content">
            <ContentContainer>
              <Outlet />
            </ContentContainer>
          </main>
        </div>
      </div>
      <OverlayManager />
      {!embedded && <StarterTour />}
      {!embedded && <RightCopilot />}
    </>
  );
}
